type RecentItemKeyFn<T> = (item: T) => string;

type RecentItemOptions<T> = {
  max?: number;
  keyFn?: RecentItemKeyFn<T>;
  storage?: Storage;
};

export const RECENT_CHATBOTS_KEY = "recent_chatbots";

const DEFAULT_MAX_ITEMS = 5;

const getDefaultKey = <T>(item: T): string => {
  if (typeof item === "string") {
    return item;
  }
  if (typeof item === "number") {
    return item.toString();
  }
  if (typeof item === "object" && item !== null && "code" in item) {
    return String((item as { code?: unknown }).code);
  }
  try {
    return JSON.stringify(item);
  } catch {
    return String(item);
  }
};

const resolveStorage = (storage?: Storage): Storage | null => {
  if (storage) {
    return storage;
  }

  try {
    return localStorage;
  } catch {
    return null;
  }
};

export const getRecentItems = <T>(key: string, storage?: Storage): T[] => {
  const resolvedStorage = resolveStorage(storage);
  if (!resolvedStorage) {
    return [];
  }

  try {
    const raw = resolvedStorage.getItem(key);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
};

export const saveRecentItem = <T>(
  key: string,
  item: T,
  options?: RecentItemOptions<T>,
): T[] => {
  const resolvedStorage = resolveStorage(options?.storage);
  if (!resolvedStorage) {
    return [];
  }

  const currentItems = getRecentItems<T>(key, resolvedStorage);
  const keyFn = options?.keyFn ?? getDefaultKey;
  const itemKey = keyFn(item);

  const filteredItems = currentItems.filter((existing) => {
    try {
      return keyFn(existing) !== itemKey;
    } catch {
      return true;
    }
  });

  const maxItems = options?.max ?? DEFAULT_MAX_ITEMS;
  const updatedItems = [item, ...filteredItems].slice(0, maxItems);

  try {
    resolvedStorage.setItem(key, JSON.stringify(updatedItems));
  } catch {
    return currentItems;
  }

  return updatedItems;
};
